import { memo, useCallback, useEffect, useState } from "react";
import { Button, IconButton, Stack } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";

import {
  Address,
  AddressType,
  Contact,
  Customer,
  CustomerAddress,
  CustomerNote,
} from "../../../models";
import { newCustomerSchema as schema } from "../schemas";
import { useAddCustomer } from "../../../hooks/customer";
import { useAddressTypes } from "../../../hooks/address-type";
import { FormContainer } from "../../layout";
import {
  FormArrayObjectSelect,
  FormPhoneNumber,
  FormSelect,
  FormSubmitButton,
  FormTextField,
} from "../../form-components";
import { AddressSearch } from "../../ui";
import { useChangeDetection } from "../../../contexts/ChangeDetectionContext";
import { useCustomerTiers } from "../../../hooks/customer-tier";
import { APPLICATION_SETTING } from "../../../globals/enums";
import { useAppSettingsContext } from "../../../contexts";
import { getAll } from "../../../api/generic-api";
import { DuplicateCustomerPrompt } from "../../../pages/customers/components/DuplicateCustomerPrompt";

interface FormData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  customerTierId: string;
  addresses: Address[];
  contacts: Contact[];
  customerNotes: CustomerNote[];
}

interface Props {
  onSubmitted?: (customer: Customer) => void;
  onDuplicateCustomerSelected?: (customerId: string) => void;
}

export const NewCustomerForm = memo((props: Props) => {
  const { onSubmitted, onDuplicateCustomerSelected } = props;
  const navigate = useNavigate();
  const { getSetting } = useAppSettingsContext();
  const { setChangeDetection } = useChangeDetection();
  const { data: addressTypes } = useAddressTypes();
  const [defaultAddressType, setDefaultAddressType] = useState<AddressType>();
  const { mutateAsync: createCustomer } = useAddCustomer();
  const { data: customerTiers } = useCustomerTiers();
  const [duplicateCandidates, setDuplicateCandidates] = useState<
    CustomerAddress[]
  >([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<FormData | null>(
    null,
  );
  const [overrideFingerprint, setOverrideFingerprint] = useState<string | null>(
    null,
  );
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const {
    handleSubmit,
    setValue,
    watch,
    control,
    getValues,
    formState: { defaultValues, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const streetNumber = watch("addresses.0.streetNumber");
  const streetName = watch("addresses.0.streetName");
  const city = watch("addresses.0.city");
  const postalCode = watch("addresses.0.postalCode");

  const {
    fields: fieldContacts,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    name: "contacts",
    control,
  } as never);

  const {
    fields: fieldNotes,
    append: appendNote,
    remove: removeNote,
  } = useFieldArray({
    name: "customerNotes",
    control,
  } as never);
  const BILLING_ADDRESS_TYPE = getSetting(
    APPLICATION_SETTING.BILLING_ADDRESS_TYPE,
  )?.settingId;

  useEffect(() => {
    const subscription = watch(
      (value, { type }) =>
        type === "change" && setChangeDetection(!isEqual(value, defaultValues)),
    );
    return () => subscription.unsubscribe();
  }, [watch, setChangeDetection, defaultValues]);

  useEffect(() => {
    if (addressTypes && BILLING_ADDRESS_TYPE) {
      setDefaultAddressType(
        addressTypes.find((x) => x.id === BILLING_ADDRESS_TYPE),
      );
      setValue(
        "addresses.0.addressTypeId",
        addressTypes.find((x) => x.id === BILLING_ADDRESS_TYPE)?.id ?? "",
      );
    }
  }, [addressTypes, setValue, BILLING_ADDRESS_TYPE]);

  useEffect(() => {
    customerTiers && setValue("customerTierId", customerTiers[0].id);
  }, [customerTiers, setValue]);

  const normalize = useCallback((value?: string) => {
    return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  }, []);

  // Creates a normalized snapshot of the key identity fields (name + address).
  // This is needed to suppress repeat duplicate prompts after "Create anyway"
  // until the user changes relevant fields.
  const buildFingerprint = useCallback(
    (values: FormData) => {
      const normalizedName = `${normalize(values.firstName)} ${normalize(
        values.lastName,
      )}`.trim();
      const normalizedAddress = [
        normalize(values.addresses?.[0]?.streetNumber),
        normalize(values.addresses?.[0]?.streetName),
        normalize(values.addresses?.[0]?.city),
        normalize(values.addresses?.[0]?.postalCode),
      ]
        .filter(Boolean)
        .join(" ");
      return `${normalizedName}|${normalizedAddress}`;
    },
    [normalize],
  );

  // Clears the saved "Create anyway" override once identity fields change.
  // Without this, the user could change name/address after an override and still
  // bypass duplicate warnings for a different customer input.
  useEffect(() => {
    const currentFingerprint = buildFingerprint(getValues());
    if (overrideFingerprint && currentFingerprint !== overrideFingerprint) {
      setOverrideFingerprint(null);
    }
  }, [
    firstName,
    lastName,
    streetNumber,
    streetName,
    city,
    postalCode,
    overrideFingerprint,
    buildFingerprint,
    getValues,
  ]);

  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!searchTerm) {
      return [];
    }
    return getAll<CustomerAddress>(
      `customers/search?searchTerm=${encodeURIComponent(searchTerm)}`,
    );
  }, []);

  // Builds duplicate candidates from name and address searches (OR semantics),
  // then deduplicates by customer id and limits results for modal readability.
  // This is needed so we can warn users about likely existing customers before create.
  const getDuplicateCandidates = useCallback(
    async (values: FormData) => {
      const nameTerm = `${values.firstName ?? ""} ${values.lastName ?? ""}`
        .trim()
        .replace(/\s+/g, " ");
      const addressTerm = [
        values.addresses?.[0]?.streetNumber ?? "",
        values.addresses?.[0]?.streetName ?? "",
        values.addresses?.[0]?.city ?? "",
      ]
        .join(" ")
        .trim()
        .replace(/\s+/g, " ");

      const queries: Promise<CustomerAddress[]>[] = [];
      if (nameTerm.length >= 3) {
        queries.push(searchCustomers(nameTerm));
      }
      if (addressTerm.length >= 6) {
        queries.push(searchCustomers(addressTerm));
      }

      if (queries.length === 0) {
        return [];
      }

      const results = await Promise.all(queries);
      const uniqueByCustomer = new Map<string, CustomerAddress>();
      results.flat().forEach((candidate) => {
        if (!uniqueByCustomer.has(candidate.customerId)) {
          uniqueByCustomer.set(candidate.customerId, candidate);
        }
      });

      return Array.from(uniqueByCustomer.values()).slice(0, 5);
    },
    [searchCustomers],
  );

  // Performs a lightweight duplicate lookup when key fields lose focus.
  // This keeps candidate data fresh before submit and supports an earlier warning UX
  // without blocking data entry or creating records.
  const checkDuplicatesOnBlur = useCallback(async () => {
    try {
      const candidates = await getDuplicateCandidates(getValues());
      setDuplicateCandidates(candidates);
    } catch {
      setDuplicateCandidates([]);
    }
  }, [getDuplicateCandidates, getValues]);

  const submitCustomer = useCallback(
    async (customer: FormData) => {
      const createdCustomer = await createCustomer(customer as Customer);
      onSubmitted && onSubmitted(createdCustomer as Customer);
      setChangeDetection(false);
    },
    [createCustomer, onSubmitted, setChangeDetection],
  );

  const onCreateAnyway = useCallback(async () => {
    if (!pendingSubmitData) {
      setShowDuplicateModal(false);
      return;
    }
    setShowDuplicateModal(false);
    setOverrideFingerprint(buildFingerprint(pendingSubmitData));
    await submitCustomer(pendingSubmitData);
    setPendingSubmitData(null);
  }, [pendingSubmitData, buildFingerprint, submitCustomer]);

  return (
    <FormContainer
      onSubmit={handleSubmit(async (customer) => {
        setIsCheckingDuplicates(true);
        try {
          const currentFingerprint = buildFingerprint(customer);
          const skipWarning =
            Boolean(overrideFingerprint) &&
            currentFingerprint === overrideFingerprint;

          if (!skipWarning) {
            const candidates = await getDuplicateCandidates(customer);
            setDuplicateCandidates(candidates);
            if (candidates.length > 0) {
              setPendingSubmitData(customer);
              setShowDuplicateModal(true);
              return;
            }
          }

          await submitCustomer(customer);
        } finally {
          setIsCheckingDuplicates(false);
        }
      })}
    >
      <h3>Customer</h3>
      <Stack direction="row" spacing={2}>
        <FormTextField
          name="firstName"
          label="First Name / Company Name"
          control={control}
          required
          fullWidth
          onBlur={checkDuplicatesOnBlur}
        />
        <FormTextField
          name="lastName"
          label="Last Name"
          control={control}
          fullWidth
          onBlur={checkDuplicatesOnBlur}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormTextField name="email" label="Email" control={control} fullWidth />
        <FormPhoneNumber name="phone" label="Phone" control={control} />
      </Stack>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {customerTiers && (
          <FormArrayObjectSelect
            name="customerTierId"
            label="Customer Tier"
            data={customerTiers}
            control={control}
            labelKey="name"
            valueKey="id"
            defaultValue={customerTiers[0].id}
            required
          />
        )}
      </Stack>

      <h3>Address</h3>
      <Stack direction="row" spacing={2}>
        <AddressSearch
          sx={{ width: 460 }}
          label="Address"
          onSelected={(selectedAddress) => {
            setValue("addresses.0", selectedAddress as Address);
            defaultAddressType &&
              setValue("addresses.0.addressTypeId", defaultAddressType.id);
          }}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormTextField
          name="addresses.0.identifierName"
          label="Identifier Name"
          control={control}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormTextField
          name="addresses.0.streetNumber"
          label="Street Number"
          control={control}
          onBlur={checkDuplicatesOnBlur}
        />
        <FormTextField
          name="addresses.0.streetName"
          label="Street Name"
          control={control}
          onBlur={checkDuplicatesOnBlur}
        />
        <FormTextField name="addresses.0.unit" label="Unit" control={control} />
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormTextField
          name="addresses.0.postalCode"
          label="Postal Code"
          control={control}
          onBlur={checkDuplicatesOnBlur}
        />
        <FormTextField
          name="addresses.0.city"
          label="City"
          control={control}
          onBlur={checkDuplicatesOnBlur}
        />
        <FormTextField
          name="addresses.0.province"
          label="Province"
          control={control}
        />
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={2}
      >
        <FormTextField
          name="addresses.0.buzzCode"
          label="Buzz Code"
          control={control}
        />
        <FormTextField
          name="addresses.0.note"
          label="Address Note"
          control={control}
        />
        {addressTypes && defaultAddressType && (
          <FormSelect
            name="addresses.0.addressTypeId"
            label="Address Type"
            labelKeys={["name"]}
            valueKey="id"
            data={addressTypes}
            control={control}
            defaultValue={defaultAddressType}
            sx={{ width: 222 }}
          />
        )}
      </Stack>

      <h3>Contacts</h3>
      <Stack>
        {fieldContacts.map((field, index) => (
          <Controller
            name={`contacts.${index}`}
            control={control}
            key={field.id}
            render={() => {
              return (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <FormTextField
                    name={`contacts.${index}.name`}
                    label="Name"
                    control={control}
                  />
                  <FormTextField
                    name={`contacts.${index}.email`}
                    label="Email"
                    control={control}
                  />
                  <FormPhoneNumber
                    name={`contacts.${index}.phone`}
                    label="Phone"
                    control={control}
                  />
                  <FormTextField
                    name={`contacts.${index}.note`}
                    label="Note"
                    control={control}
                  />
                  <IconButton onClick={() => removeContact(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              );
            }}
          />
        ))}
        <Button
          onClick={() =>
            appendContact({
              name: "",
              note: "",
              phone: "",
              email: "",
            } as Contact)
          }
          endIcon={<AddCircleIcon />}
          variant="outlined"
        >
          Add Contact
        </Button>
      </Stack>

      <h3>Customer Notes</h3>
      <Stack>
        {fieldNotes.map((field, index) => (
          <Controller
            name={`customerNotes.${index}`}
            control={control}
            key={field.id}
            render={() => (
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-start"
                alignItems="center"
              >
                <FormTextField
                  name={`customerNotes.${index}.note`}
                  label="Note"
                  control={control}
                />
                <IconButton onClick={() => removeNote(index)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            )}
          />
        ))}
        <Button
          onClick={() => appendNote({ note: "" } as CustomerNote)}
          endIcon={<AddCircleIcon />}
          variant="outlined"
        >
          Add Note
        </Button>
      </Stack>
      <FormSubmitButton text="Save" disabled={isSubmitting || isCheckingDuplicates} />
      <DuplicateCustomerPrompt
        open={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setPendingSubmitData(null);
        }}
        candidates={duplicateCandidates}
        onCreateAnyway={onCreateAnyway}
        onSelectCustomer={(customerId) => {
          setShowDuplicateModal(false);
          setPendingSubmitData(null);
          if (onDuplicateCustomerSelected) {
            onDuplicateCustomerSelected(customerId);
            return;
          }
          navigate(`/customers/${customerId}`);
        }}
      />
    </FormContainer>
  );
});
